/**
 * Converts a regex pattern into a concise, human-friendly description.
 * The result is a short noun phrase suitable for: "Must be [description]".
 */

export const getHumanFriendlyPatternDescription = (pattern: string): string => {
  const normalizedPattern = pattern.replace(/\\\\/g, "\\");
  const compactPattern = normalizedPattern.replace(/\s+/g, "");

  // Non-space pattern: ^\S+$, \S+, ^[\S]+$, [\S]+
  if (/^\^?(?:\\S|\[\\S\])\+\$?$/.test(compactPattern)) {
    return "non-space characters only";
  }

  // Time formats like ^(?:[01]\d|2[0-3]):[0-5]\d$|^24:00$
  if (
    compactPattern.includes("[0-5]\\d") &&
    compactPattern.includes("24:00") &&
    compactPattern.includes("2[0-3]")
  ) {
    return "a valid time in HH:mm format (00:00-24:00)";
  }

  if (compactPattern === "^\\d{4}-\\d{2}-\\d{2}$") {
    return "a valid date in YYYY-MM-DD format";
  }

  if (compactPattern === "^\\d{4}-\\d{2}-\\d{2}\\s\\d{2}:\\d{2}:\\d{2}$") {
    return "a valid date and time in YYYY-MM-DD HH:mm:ss format";
  }

  if (compactPattern === "^\\d{3}-\\d{3}-\\d{4}$") {
    return "a valid phone number in XXX-XXX-XXXX format";
  }

  const fixedPrefixDescription = describeFixedPrefixPattern(compactPattern);
  if (fixedPrefixDescription) {
    return fixedPrefixDescription;
  }

  const boundaryRuleDescription =
    describeBoundaryConstrainedPattern(compactPattern);
  if (boundaryRuleDescription) {
    return boundaryRuleDescription;
  }

  const allowsNull = compactPattern.includes("|null");
  let description = buildGenericDescription(normalizedPattern);
  if (allowsNull && description.endsWith(" only")) {
    description = description.slice(0, -5);
  }

  return description;
};

const describeFixedPrefixPattern = (compactPattern: string): string | null => {
  // Matches: ^[fixedClass][varClass]{min,max}$
  const pattern = /^\^\[([^\]]+)\]\[([^\]]+)\]\{(\d+),(\d+)\}\$$/;
  const match = pattern.exec(compactPattern);
  if (!match) return null;

  const fixedClass = match[1];
  const varClass = match[2];
  const min = parseInt(match[3], 10);
  const max = parseInt(match[4], 10);

  const totalMin = 1 + min;
  const totalMax = 1 + max;
  const varDescription = describeCharSet(`[${varClass}]`);
  const prefixDescription = describeLiteralOrRangeClass(fixedClass);
  const lengthStr =
    totalMin === totalMax
      ? `exactly ${totalMin}`
      : `${totalMin}\u2013${totalMax}`;

  return `${lengthStr} ${varDescription}; must start with ${prefixDescription}`;
};

const describeLiteralOrRangeClass = (charClass: string): string => {
  const hasRanges = /[A-Za-z0-9]-[A-Za-z0-9]/.test(charClass);
  if (!hasRanges) {
    const chars = charClass.split("").filter((c) => c !== "\\");
    if (chars.length === 1) return chars[0];
    const last = chars.pop()!;
    return `${chars.join(", ")}, or ${last}`;
  }
  return describeEdgeClass(charClass);
};

const describeBoundaryConstrainedPattern = (
  compactPattern: string,
): string | null => {
  const pattern =
    /^\^\(\[([^\]]+)\]\|\[([^\]]+)\]\[([^\]]+)\](?:\*|\+|\{\d+(?:,\d*)?\})\[([^\]]+)\]\)\$$/;
  const boundaryMatch = pattern.exec(compactPattern);

  if (!boundaryMatch) return null;

  const edgeA = boundaryMatch[1];
  const edgeB = boundaryMatch[2];
  const middle = boundaryMatch[3];
  const edgeC = boundaryMatch[4];

  if (edgeA !== edgeB || edgeA !== edgeC) return null;

  const edgeDescription = describeEdgeClass(edgeA);
  const middleDescription = describeCharSet(`[${middle}]`).replace(
    / characters$/,
    "",
  );

  return `${middleDescription}; must start and end with ${edgeDescription}`;
};

const describeEdgeClass = (charClass: string): string => {
  const { hasUpper, hasLower, hasDigits } = detectCharGroups(`[${charClass}]`);

  if (hasUpper && hasLower && hasDigits) return "letters or digits";
  if (hasUpper && hasLower) return "letters";
  if (hasUpper && hasDigits) return "uppercase letters or digits";
  if (hasLower && hasDigits) return "lowercase letters or digits";
  if (hasUpper) return "uppercase letters";
  if (hasLower) return "lowercase letters";
  if (hasDigits) return "digits";

  return "valid characters";
};

/** Detects which character groups are present in the pattern. */
const detectCharGroups = (
  pattern: string,
): {
  hasUpper: boolean;
  hasLower: boolean;
  hasDigits: boolean;
  hasHyphens: boolean;
  hasUnderscores: boolean;
  hasSpecials: string[];
} => {
  const hasUpper = /\[[^\]]*[A-Z][^\]]*\]/.test(pattern);
  const hasLower = /\[[^\]]*[a-z][^\]]*\]/.test(pattern);
  const hasDigits = /\[[^\]]*(?:0-9|[1-9]|\d)[^\]]*\]|\\d/.test(pattern);
  const strippedRanges = pattern.replace(/[A-Za-z0-9]-[A-Za-z0-9]/g, "");
  const hasHyphens = strippedRanges.includes("-");
  const hasUnderscores = pattern.includes("_");

  // Extract literal characters inside [...] classes by stripping ranges and escape sequences
  const classContents: string[] = [];
  const classContentRegex = /\[([^\]]+)\]/g;
  let classMatch;
  while ((classMatch = classContentRegex.exec(pattern)) !== null) {
    classContents.push(classMatch[1]);
  }
  const bareClassContent = classContents
    .join("")
    .replace(/[A-Za-z0-9]-[A-Za-z0-9]/g, "")
    .replace(/\\[A-Za-z]/g, "");

  const hasSpecials: string[] = [];
  if (
    pattern.includes("\\.") ||
    pattern.includes("[.]") ||
    bareClassContent.includes(".")
  )
    hasSpecials.push("dots");
  if (
    pattern.includes("\\*") ||
    pattern.includes("[*]") ||
    /\[[^\]]*\*[^\]]*\]/.test(pattern)
  )
    hasSpecials.push("asterisks (*)");
  if (pattern.includes("\\s") || bareClassContent.includes(" "))
    hasSpecials.push("spaces");
  if (bareClassContent.includes("+")) hasSpecials.push("plus signs");
  if (bareClassContent.includes("(") || bareClassContent.includes(")"))
    hasSpecials.push("parentheses");
  return {
    hasUpper,
    hasLower,
    hasDigits,
    hasHyphens,
    hasUnderscores,
    hasSpecials,
  };
};

/** Produces a short label like "alphanumeric", "letters", "uppercase letters and digits". */
const describeCharSet = (pattern: string): string => {
  const {
    hasUpper,
    hasLower,
    hasDigits,
    hasHyphens,
    hasUnderscores,
    hasSpecials,
  } = detectCharGroups(pattern);

  const extras: string[] = [];
  if (hasHyphens) extras.push("hyphens");
  if (hasUnderscores) extras.push("underscores");
  extras.push(...hasSpecials);
  const extraSuffix = extras.length ? `, ${extras.join(", ")}` : "";

  if (hasUpper && hasLower && hasDigits)
    return `alphanumeric characters${extraSuffix}`;
  if (hasUpper && hasLower) return `letters (A-Z, a-z)${extraSuffix}`;
  if (hasUpper && hasDigits)
    return `uppercase letters and digits${extraSuffix}`;
  if (hasLower && hasDigits)
    return `lowercase letters and digits${extraSuffix}`;
  if (hasUpper) return `uppercase letters (A-Z)${extraSuffix}`;
  if (hasLower) return `lowercase letters (a-z)${extraSuffix}`;
  if (hasDigits) return `digits (0-9)${extraSuffix}`;
  return "valid characters";
};

/** Extracts a length label like "1-25", "at least 3", or "exactly 4" from quantifiers. */
const extractLengthLabel = (pattern: string): string | null => {
  // {min,max}
  const rangePattern = /\{(\d+),(\d+)\}/;
  const rangeMatch = rangePattern.exec(pattern);
  if (rangeMatch) {
    const fixedMin = parseInt(rangeMatch[1], 10);
    const fixedMax = parseInt(rangeMatch[2], 10);
    // Only add flanking-anchor bonus when the {0,N} block sits between two standalone char classes
    // e.g. [a-zA-Z0-9][a-zA-Z0-9-_*.]{0,23}[a-zA-Z0-9]  → 1-25
    const isFlanked = /\][^{]*\{\d+,\d+\}[^[]*\[/.test(pattern);
    if (isFlanked && fixedMin === 0) {
      const total = fixedMax + 2; // two flanking chars
      return `1\u2013${total}`;
    }
    return `${fixedMin}\u2013${fixedMax}`;
  }
  // {min,}
  const minOnlyPattern = /\{(\d+),\}/;
  const minOnlyMatch = minOnlyPattern.exec(pattern);
  if (minOnlyMatch) return `at least ${minOnlyMatch[1]}`;
  // {n}
  const exactPattern = /\{(\d+)\}(?!,)/;
  const exactMatch = exactPattern.exec(pattern);
  if (exactMatch) return `exactly ${exactMatch[1]}`;
  return null;
};

/** Builds a concise description for patterns not matched by named checks. */
const buildGenericDescription = (pattern: string): string => {
  const chars = describeCharSet(pattern);
  const length = extractLengthLabel(pattern);

  if (length) {
    return `${length} ${chars}`;
  }

  // `+` means one or more; no length constraint
  return `${chars} only`;
};
